import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Tab, Tabs } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import AddButton from "../../../../../common/components/functional/addButton/AddButton";
import { CandidateRejectionDialog } from "../../../../../common/components/functional/candidateRejection/CandidateRejectionDialog";
import { CandidateRejectionDetails } from "../../../../../common/types/CandidateRejectionReason";
import AsyncContentSelector from "../../../../../common/components/functional/loadingSpinner/AsyncContentSelector";
import { CANDIDATE_INTRODUCTIONS_PAGE_SIZE } from "../../../../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../../../../common/components/functional/pagination/types";
import isEmpty from "../../../../../common/helpers/isEmpty";
import {
  Hits,
  Paginated,
  Relation,
} from "../../../../../common/types/Paginated";
import { Nullable } from "../../../../../common/types/nullable";
import { useTablePagination } from "../../../../../hooks/useTablePagination";
import useMessage from "../../../../../hooks/useMessage";
import i18n from "../../../../../i18n";
import { Talent } from "../../../../talents/talentAPI";
import {
  approveIntroductionWithoutOrder,
  cancelIntroductionWithoutOrder,
  fetchCandidateIntroductions,
} from "../../../../talents/talentsSlice";
import { TalentStatus } from "../../../../talents/types/common";
import {
  ACTIVE_WITHOUT_ORDER_INTRODUCTION_STATUSES,
  TalentIntroduction,
} from "../../../../talents/types/queries";
import EmptyStateText from "../../EmptyStateText";
import TalentSummaryCard from "../../TalentSummaryCard";
import { CONTAINER_MIN_HEIGHT, INTRODUCTION_ROW_HEIGHT } from "../../constants";
import { TalentIntroductionRow } from "../../types";
import ShowAllButton from "./ShowAllButton";
import sortTalentIntroductionsByIntroducedAt from "./sortTalentIntroductionsByIntroducedAt";
import TalentIntroducedToCustomerRows from "./TalentIntroducedToCustomerRows";
import TalentIntroductionsWithOrderTab from "./TalentIntroductionsWithOrderTab";
import TalentIntroduceToCustomerDialog from "./talentIntroduceToCustomerDialog/TalentIntroduceToCustomerDialog";
import TalentIntroductionsToCustomerDialog from "./talentIntroductionsDialog/TalentIntroductionsToCustomerDialog";

type Props = {
  talent: Talent;
  previewMode?: boolean;
  introductionsWithoutOrder?: Paginated<TalentIntroduction[]>;
  introductionsWithOrder?: Paginated<TalentIntroduction[]>;
};

const TalentIntroducedToCustomer = ({
  talent,
  previewMode = false,
  introductionsWithoutOrder,
  introductionsWithOrder,
}: Props) => {
  const [selectedTab, setSelectedTab] = useState<"withoutOrder" | "withOrder">(
    "withoutOrder",
  );
  const [orderIntroductionCount, setOrderIntroductionCount] = useState(0);
  const [newIntroductionDialogOpen, setNewIntroductionDialogOpen] =
    useState(false);
  const [introductionsDialogOpen, setIntroductionsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rejectionWorkLocationId, setRejectionWorkLocationId] = useState<string | null>(null);
  const [hits, setHits] = useState<Hits>({
    relation: Relation.DEFAULT,
    value: 0,
  });
  const [cursor, setCursor] = useState<Nullable<string>>(null);
  const [candidateIntroductions, setCandidateIntroductions] = useState<
    TalentIntroductionRow[]
  >([]);

  const UILanguage = i18n.language;
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();

  const { workTitle: translatedWorkTitles } = useAppSelector(
    (state) => state.localized,
  );

  const pageSize = CANDIDATE_INTRODUCTIONS_PAGE_SIZE;

  const dispatchFetchCandidateIntroductions = useCallback(
    (cursor?: string) => {
      setIsLoading(true);
      dispatch(
        fetchCandidateIntroductions({
          ...(cursor && { cursor }),
          talentId: talent.id,
          pageSize,
          statuses: ACTIVE_WITHOUT_ORDER_INTRODUCTION_STATUSES,
        }),
      )
        .then((action) => {
          if (isFulfilled(action)) {
            const formattedRows = action.payload.data.map((row) => ({
              ...row,
              workTitles: row.workTitleIds.map((id) => ({
                id: id,
                name: translatedWorkTitles[id] || id,
              })),
            }));
            setCandidateIntroductions((previous) =>
              sortTalentIntroductionsByIntroducedAt([
                ...previous,
                ...formattedRows,
              ]),
            );
            setCursor(action.payload.cursor);
            setHits(action.payload.hits);
          }
        })
        .finally(() => setIsLoading(false));
    },
    [talent.id, translatedWorkTitles, dispatch, pageSize],
  );

  // translate work titles when UI language is changed
  useEffect(() => {
    setCandidateIntroductions((prev) =>
      prev.map((introduction) => ({
        ...introduction,
        workTitles: introduction.workTitles.map((workTitle) => ({
          id: workTitle.id,
          name: translatedWorkTitles[workTitle.id] || workTitle.id,
        })),
      })),
    );
  }, [UILanguage, translatedWorkTitles]);

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<TalentIntroductionRow>({
      data: candidateIntroductions,
      cursor,
      hits,
      pageSize: parseInt(pageSize, 10),
      preventDispatch: true,
      fetchNext: (cursor) => {
        dispatchFetchCandidateIntroductions(cursor);
      },
    });

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize: parseInt(CANDIDATE_INTRODUCTIONS_PAGE_SIZE, 10),
  };

  useEffect(() => {
    if (previewMode) {
      const introductions = introductionsWithoutOrder?.data ?? [];
      setCandidateIntroductions(
        sortTalentIntroductionsByIntroducedAt(
          introductions.map((introduction) => ({
            ...introduction,
            workTitles: introduction.workTitleIds.map((id) => ({
              id,
              name: translatedWorkTitles[id] || id,
            })),
          })),
        ),
      );
      setCursor(null);
      setHits(
        introductionsWithoutOrder?.hits ?? {
          relation: Relation.DEFAULT,
          value: 0,
        },
      );
      return;
    }

    dispatchFetchCandidateIntroductions();
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, talent.id, previewMode, introductionsWithoutOrder]);

  const removeIntroduction = (index: number) => {
    setCandidateIntroductions((introductions) =>
      introductions.filter(
        (_, introductionIndex) => introductionIndex !== index,
      ),
    );
    setHits((previousHits) => ({
      ...previousHits,
      value: Math.max(0, previousHits.value - 1),
    }));
  };

  const cancelIntroduction = (index: number) => {
    const cancelledIntroduction = candidateIntroductions[index];

    if (cancelledIntroduction) {
      dispatch(
        cancelIntroductionWithoutOrder({
          talentId: talent.id,
          workLocationId: cancelledIntroduction.workLocationId,
        }),
      )
        .then((action) => {
          if (isFulfilled(action)) {
            removeIntroduction(index);
            sendMessage(t("profileSummary.introductionCancelled"), "success");
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const approveIntroduction = async (
    index: number,
    approved: boolean,
    details: CandidateRejectionDetails = {},
  ) => {
    const introduction = candidateIntroductions[index];

    const payload = {
      workLocationId: introduction.workLocationId,
      talentId: talent.id,
      approved,
      ...details,
    };

    setIsLoading(true);
    const action = await dispatch(approveIntroductionWithoutOrder(payload));
    setIsLoading(false);
    if (!isFulfilled(action)) return false;
    removeIntroduction(index);
    sendMessage(
      t(approved ? "profileSummary.introductionApproved" : "profileSummary.introductionRejected"),
      "success",
    );
    return true;
  };

  const updateLocalIntroductions = (
    introduction: TalentIntroductionRow,
    existingIndex: number,
  ) => {
    const introductionWithTranslatedWorkTitle = introduction.workTitles.map(
      (workTitle) => ({
        ...workTitle,
        name: translatedWorkTitles[workTitle.id] || workTitle.id,
      }),
    );
    const introductionWithTranslation = {
      ...introduction,
      workTitles: introductionWithTranslatedWorkTitle,
    };

    if (existingIndex === -1) {
      // add new introduction
      setCandidateIntroductions(
        sortTalentIntroductionsByIntroducedAt([
          introductionWithTranslation,
          ...candidateIntroductions,
        ]),
      );
      setHits({ ...hits, value: hits.value + 1 });
    } else {
      // update existing introduction
      setCandidateIntroductions(
        sortTalentIntroductionsByIntroducedAt(
          candidateIntroductions.map((candidateIntroduction, index) =>
            index === existingIndex
              ? introductionWithTranslation
              : candidateIntroduction,
          ),
        ),
      );
    }
  };

  const toggleNewIntroductionDialog = () => {
    setNewIntroductionDialogOpen(!newIntroductionDialogOpen);
    resetPagination();
  };

  const toggleIntroductionsDialog = () =>
    setIntroductionsDialogOpen(!introductionsDialogOpen);

  const selectActionForIntroduction = (id: string, action: string) => {
    // id is index of the introduction element as string
    const index = parseInt(id);
    if (action === "reject") {
      setRejectionWorkLocationId(candidateIntroductions[index].workLocationId);
      return;
    }
    setIsLoading(true);

    const actions: Record<string, () => void> = {
      cancel: () => cancelIntroduction(index),
      accept: () => approveIntroduction(index, true),
    };

    return actions[action]();
  };

  const displayShowAll = hits.value > 3;
  const containerMinHeight = isEmpty(candidateIntroductions)
    ? CONTAINER_MIN_HEIGHT
    : `calc(${INTRODUCTION_ROW_HEIGHT} * ${displayShowAll ? 4 : hits.value + 1})`;

  const displayedIntroductions = candidateIntroductions.slice(0, 3);
  const addButtonDisabled =
    isLoading || talent.account.status !== TalentStatus.ACTIVE;

  return (
    <TalentSummaryCard title={t("profileSummary.introduced")} divider>
      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value)}
        variant="fullWidth"
        sx={{ minHeight: "36px", marginBottom: "12px" }}
      >
        <Tab
          value="withoutOrder"
          label={t("profileSummary.introductionsWithoutOrderWithCount", {
            count: hits.value,
          })}
          sx={{ minHeight: "36px", padding: "6px" }}
        />
        <Tab
          value="withOrder"
          label={t("profileSummary.activeOrderIntroductionsWithCount", {
            count: orderIntroductionCount,
          })}
          sx={{ minHeight: "36px", padding: "6px" }}
        />
      </Tabs>
      <Box sx={{ display: selectedTab === "withoutOrder" ? "block" : "none" }}>
        <AsyncContentSelector
          isLoading={isLoading}
          emptyStateCondition={isEmpty(candidateIntroductions)}
          emptyStateContent={<EmptyStateText />}
          ContainerProps={{
            sx: { minHeight: containerMinHeight },
          }}
        >
          <TalentIntroducedToCustomerRows
            rows={displayedIntroductions}
            selectActionForIntroduction={
              previewMode ? undefined : selectActionForIntroduction
            }
          />
          {!previewMode && (
            <ShowAllButton
              visible={displayShowAll}
              disabled={isLoading}
              onClick={toggleIntroductionsDialog}
            />
          )}
        </AsyncContentSelector>
        {!previewMode && (
          <>
            <AddButton
              sx={{ marginBottom: "8px" }}
              label={t("profileSummary.introduceToCustomer")}
              onClick={toggleNewIntroductionDialog}
              disabled={addButtonDisabled}
            />
            <TalentIntroduceToCustomerDialog
              open={newIntroductionDialogOpen}
              toggleDialog={toggleNewIntroductionDialog}
              updateLocalIntroductions={updateLocalIntroductions}
              candidateIntroductions={candidateIntroductions}
            />
            <TalentIntroductionsToCustomerDialog
              open={introductionsDialogOpen}
              toggleDialog={toggleIntroductionsDialog}
              rows={pageData}
              selectActionForIntroduction={selectActionForIntroduction}
              isLoading={isLoading}
              pagination={paginationProps}
            />
            <CandidateRejectionDialog
              open={rejectionWorkLocationId !== null}
              title={t("order:customerRejected")}
              talentName={`${talent.account.firstName} ${talent.account.lastName}`}
              onClose={() => setRejectionWorkLocationId(null)}
              onReject={async (details) => {
                const index = candidateIntroductions.findIndex(
                  (introduction) => introduction.workLocationId === rejectionWorkLocationId,
                );
                return index >= 0 && await approveIntroduction(index, false, details);
              }}
            />
          </>
        )}
      </Box>
      <Box sx={{ display: selectedTab === "withOrder" ? "block" : "none" }}>
        <TalentIntroductionsWithOrderTab
          talent={talent}
          previewMode={previewMode}
          introductions={introductionsWithOrder}
          onTotalChange={setOrderIntroductionCount}
        />
      </Box>
    </TalentSummaryCard>
  );
};

export default TalentIntroducedToCustomer;
